"use client";

import { IconCheck, IconInfo } from "@decibeltrade/react-icons";
import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { Card } from "@/components/card/card";
import { Tooltip } from "@/components/tooltip";
import { NumberFormatter } from "@/utils/number-formatter";

interface OverviewCardProps {
  totalPoints: string;
  rank: string;
  referrals: string;
  referralPoints?: string;
  lastUpdated?: string;
  seasonNumber: number;
  confirmed?: boolean;
  predepositUsdcReward?: number;
}

export function OverviewCard({
  totalPoints,
  rank,
  referrals,
  referralPoints,
  seasonNumber,
  confirmed = true,
  predepositUsdcReward,
}: OverviewCardProps) {
  const t = useTranslations("points.overview");

  return (
    <Card
      background="gradient"
      className={stack({
        p: { base: "16", md: "24" },
        w: "full",
        gap: { base: "20", md: "24" },
        rounded: "350",
      })}
    >
      <div className={stack({ gap: "4", alignItems: "flex-start" })}>
        <h2
          className={css({
            textStyle: { base: "heading.md", md: "heading.lg" },
            color: "text.primary",
          })}
        >
          {t("title")}
        </h2>
        {/* TODO: wire lastUpdated to cron job timestamp */}
        {/* <span
          className={css({
            textStyle: "body.md",
            color: "text.primary",
          })}
        >
          {t("lastUpdated", { date: lastUpdated })}
        </span> */}
      </div>

      <div className={flex({ gap: "12", w: "full", flexWrap: "wrap" })}>
        {confirmed ? (
          <ConfirmedCard label={t("seasonPointsTotal", { seasonNumber: seasonNumber - 1 })} />
        ) : (
          <MetricCard
            label={t("seasonPointsTotal", { seasonNumber: seasonNumber - 1 })}
            value={"\u2014"}
          />
        )}
        <MetricCard
          label={t("totalPoints", { seasonNumber })}
          value={totalPoints}
          unit={t("unitAmp")}
          tooltip={t("totalPointsTooltip")}
        />
        <MetricCard label={t("rank")} value={rank} />
        <MetricCard label={t("referrals")} value={referrals} unit={t("unitJoined")} />
        {referralPoints != null && (
          <MetricCard
            label={t("referralPoints")}
            value={referralPoints}
            unit={t("unitAmp")}
            tooltip={t("referralPointsTooltip")}
          />
        )}
        {!!predepositUsdcReward && (
          <MetricCard
            label={t("predepositRewards")}
            value={NumberFormatter.compactHeading(predepositUsdcReward)}
            unit={t("unitUsdc")}
            tooltip={t("predepositRewardsTooltip")}
          />
        )}
      </div>
    </Card>
  );
}

interface MetricCardProps {
  label: string;
  value: string;
  unit?: string;
  tooltip?: string;
}

function MetricCard({ label, value, unit, tooltip }: MetricCardProps) {
  return (
    <div className={cardStyles}>
      <div className={flex({ gap: "4", alignItems: "center", minW: "0", w: "full" })}>
        <span
          className={css({
            textStyle: { base: "highlight.lg", md: "highlight.lg" },
            color: "text.tertiary",
            textTransform: "uppercase",
            truncate: true,
          })}
        >
          {label}
        </span>
        {tooltip && (
          <Tooltip content={tooltip} placement="top">
            <span className={css({ display: "flex", cursor: "help" })}>
              <IconInfo
                className={css({
                  w: { base: "14", md: "16" },
                  h: { base: "14", md: "16" },
                  color: "text.tertiary",
                })}
              />
            </span>
          </Tooltip>
        )}
      </div>
      <div className={css({ truncate: true, w: "full" })}>
        <span
          className={css({
            textStyle: { base: "subheading.lg", md: "subheading.xl" },
            fontWeight: "light",
            color: "text.primary",
          })}
        >
          {value}
        </span>
        {unit && (
          <span
            className={css({
              textStyle: { base: "highlight.xl", md: "heading.md" },
              color: "text.tertiary",
            })}
          >
            {" "}
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

interface ConfirmedCardProps {
  label: string;
}

function ConfirmedCard({ label }: ConfirmedCardProps) {
  const t = useTranslations("points.overview");

  return (
    <div className={cardStyles}>
      <span
        className={css({
          textStyle: { base: "highlight.lg", md: "highlight.lg" },
          color: "text.tertiary",
          textTransform: "uppercase",
        })}
      >
        {label}
      </span>
      <div className={flex({ gap: { base: "4", md: "8" }, alignItems: "center" })}>
        <IconCheck
          className={css({
            w: { base: "24", md: "28", lg: "32" },
            h: { base: "24", md: "28", lg: "32" },
            color: "brandYellow.550",
            flexShrink: "0",
          })}
        />
        <span
          className={css({
            textStyle: { base: "subheading.lg", md: "subheading.lg", lg: "display.xs" },
            fontWeight: "light",
            color: "text.primary",
          })}
        >
          {t("confirmed")}
        </span>
      </div>
    </div>
  );
}

const cardStyles = stack({
  flex: "1",
  flexBasis: "[250px]",
  bg: "background.primary",
  rounded: "350",
  px: { base: "12", md: "16" },
  py: { base: "12", md: "20" },
  gap: "2",
  alignItems: "flex-start",
  justifyContent: "center",
  textAlign: "left",
});
